import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import SelectOptionsModal from '@components/Projects/Modals/SelectOptionsModal.js';
import { DropdownIcon } from '@icons/index.js';
import { COLUMN_EVENT_TYPES, columnUpdate } from '@signals/project/index.js';
import { UIButton, UIDiv, UIText } from '@ui';
import { getContrastTextColor } from '@utils/color';
import { updateColumnValue } from '@utils/columnUtils.js';

export default class SelectCell extends UIDiv {
  constructor({
    column,
    item = {},
    value = '',
    canEdit = true,
    currentFolderId = null,
    onValueChange = null,
  }) {
    super();

    this.column = column;
    this.item = item;
    this.value = value;
    this.isEditMode = false;
    this.canEdit = canEdit;
    this.currentFolderId = currentFolderId;
    this.onValueChange = onValueChange; // Callback for value changes instead of API calls

    // --- Initialize Data ---
    this.#initializeOptions();

    columnUpdate.add(data => {
      this.#handleColumnEvent(data);
    });

    this.#render();
  }
  /**
   * Processes options from the column data, sorting and filtering them.
   * This logic is now centralized here instead of being duplicated.
   * @private
   */
  #initializeOptions() {
    const statusOptions = this.column.options || {};

    const getOrder = val =>
      typeof val === 'object' && val.order !== undefined ? val.order : 999;

    this.optionsArray = Object.entries(statusOptions)
      .sort(([, aValue], [, bValue]) => getOrder(aValue) - getOrder(bValue))
      .filter(
        ([, value]) => !(typeof value === 'object' && value.isDeactivated),
      )
      .map(([key, value]) => ({
        value: key,
        label: typeof value === 'string' ? value : value.label,
        color: typeof value === 'string' ? null : value.color,
        order: typeof value === 'object' ? value.order : undefined,
        isDeactivated: typeof value === 'object' ? value.isDeactivated : false,
      }));

    this.selectedOption = this.optionsArray.find(
      opt => opt.value === this.value,
    );
  }

  /**
   * Handles column events and refreshes the cell if options were updated for this column.
   * @param {Object} eventData - The event data
   * @private
   */
  #handleColumnEvent(eventData) {
    const { type, columnId } = eventData;

    // Only respond to options update events for this specific column
    if (
      type === COLUMN_EVENT_TYPES.OPTIONS_UPDATED &&
      columnId === this.column.id
    ) {
      // Update the column reference with new options
      if (eventData.column) {
        this.column = eventData.column;
      }
      // Refresh the cell to reflect the updated options
      this.#refreshOptions();
    }
  }

  /**
   * Cleanup method to unsubscribe from events when the component is destroyed.
   * Call this method when the SelectCell is no longer needed.
   */
  destroy() {
    columnUpdate.remove(this.#handleColumnEvent);
  }

  #render() {
    this.clear();

    if (!this.canEdit) {
      this.#renderUneditableInput();
      return;
    }

    // Create the trigger element based on state
    const trigger =
      this.value == null || this.value.trim() === ''
        ? this.#createEmptyTrigger()
        : this.#createSelectedTrigger();

    // Create popover once and attach it
    const popoverContent = this.#createPopoverContent();
    trigger.setAttribute('tabindex', '0'); // Ensure always focusable
    trigger.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: trigger,
        content: popoverContent,
        placement: 'bottom-start',
        hideOnSelect: true,
        hideOnScroll: true,
      });
    });

    trigger.onKeyDown(event => {
      if (event.key === 'Enter' && !this.isEditMode) {
        event.preventDefault();
        trigger.dom.click();
      }
    });

    this.add(trigger);
  }

  /**
   * Creates the trigger element for when no option is selected.
   * @returns {UIDiv} The trigger element.
   * @private
   */
  #createEmptyTrigger() {
    const emptyContainer = new UIDiv();
    emptyContainer.addClass(
      'group hover:border hover:border-gray-300 hover:bg-gray-50 rounded-md min-h-[34px] cursor-pointer flex items-center justify-between px-2',
    );

    const placeholder = new UIText(
      i18next.t('pages.projects.cells.select.selectOption'),
    );
    placeholder.addClass('text-gray-500 text-sm');
    emptyContainer.add(placeholder);

    emptyContainer.add(
      new DropdownIcon({ size: 16, color: 'none', strokeColor: '#9CA3AF' }),
    );

    return emptyContainer;
  }

  /**
   * Creates the trigger element for when an option is selected.
   * @returns {UIDiv} The trigger element.
   * @private
   */
  #createSelectedTrigger() {
    const selectedContainer = new UIDiv();

    const bgColor = this.selectedOption?.color || '#f3f4f6';
    const textColor = getContrastTextColor(bgColor); // Using external helper

    selectedContainer.addClass(
      'group hover:opacity-80 rounded-md min-h-[34px] cursor-pointer flex items-center justify-between px-2 gap-2 transition-opacity',
    );
    selectedContainer.setStyle('background-color', [bgColor]);

    const label = new UIText(this.selectedOption?.label || this.value);
    label.addClass('truncate flex-1 text-sm font-medium');
    label.setStyle('color', [textColor]);
    selectedContainer.add(label);

    selectedContainer.add(
      new DropdownIcon({ size: 16, color: 'none', strokeColor: textColor }),
    );

    return selectedContainer;
  }

  // --- Popover Content Creation ---

  /**
   * Creates the main content container for the popover.
   * @returns {UIDiv} The popover content element.
   * @private
   */
  #createPopoverContent() {
    const content = new UIDiv();
    content.addClass('py-2 min-w-48');

    // Builder methods for clarity
    content.add(this.#createUnselectOption());
    content.add(this.#createSeparator());

    this.optionsArray.forEach(option => {
      content.add(this.#createOptionItem(option));
    });

    content.add(this.#createSeparator());
    content.add(this.#createEditOptionsButton());

    return content;
  }

  /**
   * Creates a single option item for the popover list.
   * @param {object} option - The option data.
   * @returns {UIDiv} The option item element.
   * @private
   */
  #createOptionItem(option) {
    const optionItem = new UIDiv();
    optionItem.addClass(
      'px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2',
    );
    optionItem.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#selectOption(option);
        Popover.hide();
      }
    });
    optionItem.onClick(() => this.#selectOption(option));
    optionItem.setAttribute('tabindex', '0');

    const colorDot = new UIDiv();
    colorDot.addClass('w-4 h-4 rounded-full flex-shrink-0');
    colorDot.setStyle('background-color', [option.color] || ['#f3f4f6']);

    const label = new UIText(option.label);
    label.addClass('truncate');

    optionItem.add(colorDot, label);
    return optionItem;
  }

  /**
   * Creates the "Unselected" option for the popover.
   * @returns {UIDiv}
   * @private
   */
  #createUnselectOption() {
    const unselectOption = new UIDiv();
    unselectOption.addClass(
      'px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2',
    );
    unselectOption.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#selectOption(null);
        Popover.hide();
      }
    });
    unselectOption.onClick(() => this.#selectOption(null));
    unselectOption.setAttribute('tabindex', '0');

    const unselectDot = new UIDiv();
    unselectDot.addClass(
      'w-4 h-4 rounded-full border border-gray-300 bg-gray-100 flex-shrink-0',
    );

    const unselectText = new UIText(
      i18next.t('pages.projects.cells.select.deselect'),
    );
    unselectText.addClass('text-gray-600');

    unselectOption.add(unselectDot, unselectText);
    return unselectOption;
  }

  /**
   * Creates the "Edit Options" button for the popover.
   * @returns {UIButton} The button element.
   * @private
   */
  #createEditOptionsButton() {
    const editButton = new UIButton(
      i18next.t('pages.projects.cells.select.editOptions'),
    );
    editButton.addClass(
      '!filter-none w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors text-left',
    );
    editButton.onClick(() => this.#openEditOptionsModal());
    return editButton;
  }

  async #selectOption(option) {
    const newValue = option ? option.value : '';

    if (newValue === this.value) return;

    try {
      // If onValueChange callback is provided, use it instead of API call
      if (this.onValueChange) {
        this.onValueChange(newValue);
        // Update the local value
        this.value = newValue;
        this.selectedOption = option;

        setTimeout(() => {
          this.#render();
        }, 0); // Ensure render happens after value update
      } else {
        // Update the value via API if we have the necessary information
        const entityType = !this.item.reconstructions ? 'folder' : 'file';
        const entityId = this.item.id || this.item.sharingId;

        await updateColumnValue(this.column.id, entityType, entityId, newValue);

        // Update the local value
        this.value = newValue;
        this.selectedOption = option;

        if (this.item) {
          this.item[this.column.key] = newValue;
        }

        this.#render();
      }
    } catch (error) {
      toast.error(i18next.t('pages.projects.toast.failed_column_update'));
      console.error('Failed to update column value:', error);
    }
  }

  #openEditOptionsModal() {
    const modal = new SelectOptionsModal(this.column, this.currentFolderId);
    // Individual callback for backward compatibility
    // All SelectCell instances will be updated via the global column event system
    modal.onOptionsUpdated = () => this.#refreshOptions();
    modal.show();
  }

  // Method to refresh options and re-render the cell
  #refreshOptions() {
    // Re-initialize options to reflect any changes made in the modal
    this.#initializeOptions();
    // Re-render the cell with updated options
    this.#render();
  }

  #renderUneditableInput() {
    const textContainer = new UIDiv();

    // Show background color for selected option (read-only)
    if (this.selectedOption) {
      const color =
        this.selectedOption.color ||
        this.colorMap[this.optionsArray.indexOf(this.selectedOption)];
      const textColor = getContrastTextColor(color);

      textContainer.addClass('min-h-[34px] flex items-center px-2 rounded-md');
      textContainer.setStyle('background-color', [color]);

      const textContent = new UIText(this.selectedOption.label);
      textContent.addClass('truncate flex-1 text-sm font-medium');
      textContent.setStyle('color', [textColor]);
      textContainer.add(textContent);
    } else if (this.value) {
      // Fallback for when option is not found but value exists
      textContainer.addClass('min-h-[34px] flex items-center px-2');
      const textContent = new UIText(this.value);
      textContent.addClass('truncate flex-1 text-sm text-gray-500');
      textContainer.add(textContent);
    } else {
      // No value selected
      textContainer.addClass('min-h-[34px] flex items-center px-2');
    }

    this.add(textContainer);
  }

  #createSeparator() {
    const separator = new UIDiv();
    separator.addClass('border-t border-gray-200 my-1');
    return separator;
  }
}
